import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Package, Search, X } from "lucide-react";
import Skeleton from "@/components/Skeleton";
import HeroMedia from "@/components/HeroMedia";
import { useSiteContent } from "@/context/SiteContentContext";
import { useTranslation } from "react-i18next";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/bea3c376-67c0-4264-85e7-8b27d546bc57/images/c19ead953dcd679087fbb2cf927bc9b2259e1471c6783da5a8b2fae941f235ca.png";

export default function HomePage() {
    const [categories, setCategories] = useState(null);
    const [brands, setBrands] = useState([]);
    const [stats, setStats] = useState({ categories: 0, products: 0 });
    const { content } = useSiteContent();
    const { t } = useTranslation();
    const nav = useNavigate();

    // Quick search state
    const [quickQuery, setQuickQuery] = useState("");
    const [recent, setRecent] = useState([]);
    const [suggestions, setSuggestions] = useState(null);
    const POPULAR = ["703245", "GT1549S", "Renault", "GT2056V", "Ford", "Volkswagen"];

    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem("tc_recent_searches") || "[]");
            setRecent(Array.isArray(stored) ? stored.slice(0, 6) : []);
        } catch {
            setRecent([]);
        }
    }, []);

    const submitQuickSearch = (q) => {
        const query = (q || quickQuery).trim();
        if (!query) return;
        try {
            const next = [query, ...recent.filter((r) => r !== query)].slice(0, 6);
            localStorage.setItem("tc_recent_searches", JSON.stringify(next));
            setRecent(next);
        } catch {}
        nav(`/catalog?q=${encodeURIComponent(query)}`);
    };

    const clearRecent = () => {
        localStorage.removeItem("tc_recent_searches");
        setRecent([]);
    };

    useEffect(() => {
        const q = quickQuery.trim();
        if (q.length < 2) { setSuggestions(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/products", { params: { q, per_page: 5 } });
                setSuggestions(data);
            } catch {
                setSuggestions([]);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [quickQuery]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/categories");
                setCategories(data);
                const totalProducts = data.reduce((s, c) => s + (c.product_count || 0), 0);
                setStats({ categories: data.length, products: totalProducts });
            } catch {
                setCategories([]);
            }
            try {
                const { data } = await api.get("/brands");
                setBrands(data);
            } catch {
                setBrands([]);
            }
        })();
    }, []);

    const oemBrands = brands.filter((b) => b.kind === "oem");
    const aftermarketBrands = brands.filter((b) => b.kind === "aftermarket");

    return (
        <div className="bg-white" data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-slate-200" data-testid="hero-section">
                <div
                    className="absolute inset-0"
                    data-testid="hero-background"
                >
                    {(() => {
                        const mediaUrl = content.hero_background_url || HERO_IMG;
                        const isVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(mediaUrl);
                        const opacity = content.hero_background_url
                            ? (content.hero_background_opacity ?? 0.18)
                            : 0.22;
                        if (isVideo) {
                            return (
                                <video
                                    src={mediaUrl}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-full object-cover"
                                    style={{ opacity }}
                                    data-testid="hero-video"
                                />
                            );
                        }
                        return (
                            <div
                                className="w-full h-full bg-cover bg-center"
                                style={{ backgroundImage: `url(${mediaUrl})`, opacity }}
                            />
                        );
                    })()}
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-transparent" />
                <div className="hero-fx-grid absolute inset-0 pointer-events-none" aria-hidden="true" />
                <div className="hero-fx-glow absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-[1440px] mx-auto px-4 py-16 md:py-24 grid md:grid-cols-[1.4fr_1fr] gap-10 items-end">
                    <div className={`relative z-10 ${
                        content.hero_text_align === "center"
                            ? "md:col-span-2 text-center max-w-3xl mx-auto"
                            : content.hero_text_align === "right"
                            ? "md:col-start-1 text-right ml-auto"
                            : ""
                    }`} data-testid="hero-content">
                        {content.hero_kicker && (
                            <div
                                className="label-kicker mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-slate-200 shadow-sm fx-reveal"
                                style={content.hero_kicker_color ? { color: content.hero_kicker_color } : undefined}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
                                {content.hero_kicker}
                            </div>
                        )}
                        <h1
                            className="font-display font-black text-4xl sm:text-6xl md:text-7xl uppercase leading-[0.9] tracking-tighter text-slate-900 break-words fx-reveal"
                            style={content.hero_title_color ? { color: content.hero_title_color } : undefined}
                        >
                            {content.hero_line1}<br />
                            <span className="hero-fx-gradient-text">{content.hero_line2}</span><br />
                            {content.hero_line3}
                        </h1>
                        {content.hero_subtitle && (
                            <p
                                className={`mt-6 max-w-xl leading-relaxed ${content.hero_text_align === "center" ? "mx-auto" : ""} ${content.hero_text_align === "right" ? "ml-auto" : ""}`}
                                style={{ color: content.hero_subtitle_color || "#475569" }}
                            >
                                {content.hero_subtitle}
                            </p>
                        )}
                    </div>
                    <HeroMedia url={content.hero_media_url} />
                </div>
            </section>

            {/* QUICK SEARCH */}
            <section className="qs-strip relative text-white" data-testid="quick-search-section">
                <div className="relative max-w-[1200px] mx-auto px-4 py-5 md:py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-10">
                            <div className="shrink-0">
                                <div className="label-kicker mb-1 !text-slate-400 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] shadow-[0_0_10px_hsl(var(--primary))]" />
                                    {t("home.quick_search_kicker")}
                                </div>
                                <h2 className="font-display font-black text-xl md:text-2xl uppercase tracking-tight text-white" data-testid="quick-search-title">
                                    {t("home.quick_search_title")}
                                </h2>
                            </div>
                            <form
                                onSubmit={(e) => { e.preventDefault(); submitQuickSearch(); }}
                                className="relative text-slate-900 flex-1 min-w-0"
                                data-testid="quick-search-form"
                            >
                                <div className="nav-search-ring group rounded-full p-[1.5px]">
                                <div className="flex items-center rounded-full bg-white/95 backdrop-blur-md p-1.5">
                                    <Search className="w-5 h-5 ml-3 md:ml-4 shrink-0 text-slate-400 group-focus-within:text-[hsl(var(--primary))] transition-colors" />
                                    <input
                                        type="text"
                                        value={quickQuery}
                                        onChange={(e) => setQuickQuery(e.target.value)}
                                        placeholder={t("home.quick_search_placeholder")}
                                        className="flex-1 min-w-0 px-3 md:px-4 py-3 text-base font-mono bg-transparent outline-none"
                                        data-testid="quick-search-input"
                                        autoComplete="off"
                                    />
                                    {quickQuery && (
                                        <button
                                            type="button"
                                            onClick={() => { setQuickQuery(""); setSuggestions(null); }}
                                            className="p-2 text-slate-400 hover:text-slate-900"
                                            data-testid="quick-search-clear"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        className="nav-search-btn shrink-0 relative overflow-hidden rounded-full text-white pl-4 pr-3 md:pl-6 md:pr-5 py-3 font-display font-black uppercase tracking-wider text-sm flex items-center gap-2"
                                        data-testid="quick-search-submit"
                                    >
                                        <span className="relative z-10">{t("common.search")}</span>
                                        <ArrowRight className="relative z-10 w-4 h-4" />
                                    </button>
                                </div>
                                </div>

                                {/* Live suggestions dropdown */}
                                {suggestions !== null && quickQuery.trim().length >= 2 && (
                                    <div
                                        className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-slate-900 shadow-[4px_4px_0_0_rgba(15,23,42,0.8)] z-20 max-h-[400px] overflow-auto"
                                        data-testid="quick-search-suggestions"
                                    >
                                        {suggestions.length === 0 ? (
                                            <div className="p-4 text-sm font-mono text-slate-500 text-center">
                                                {t("home.no_matches")}
                                            </div>
                                        ) : (
                                            <>
                                                {suggestions.map((p) => (
                                                    <Link
                                                        key={p.id}
                                                        to={`/product/${p.id}`}
                                                        onClick={() => submitQuickSearch(quickQuery)}
                                                        className="flex items-center gap-3 p-2 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                                                        data-testid={`quick-suggestion-${p.id}`}
                                                    >
                                                        <div className="w-10 h-10 bg-slate-50 border border-slate-200 shrink-0">
                                                            {p.image_url ? (
                                                                <img src={p.image_url} alt="" className="w-full h-full object-contain p-0.5" onError={(e) => (e.currentTarget.style.display = "none")} />
                                                            ) : (
                                                                <Package className="w-5 h-5 text-slate-300 m-auto mt-2.5" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-display font-bold text-sm uppercase truncate">{p.name}</div>
                                                            <div className="font-mono text-[11px] text-slate-500 truncate">{p.category_name}</div>
                                                        </div>
                                                        <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                                                    </Link>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => submitQuickSearch()}
                                                    className="w-full p-2.5 text-center font-display font-bold uppercase text-xs tracking-wider bg-slate-50 hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
                                                    data-testid="quick-search-view-all"
                                                >
                                                    {t("home.view_all_results")} "{quickQuery}" →
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </form>
                    </div>
                </div>
            </section>

            {/* HERO CTA buttons (moved from inside the hero so they sit below the USP strip) */}
            <section className="border-b border-slate-200 bg-white" data-testid="hero-cta-section">
                <div className="max-w-[1440px] mx-auto px-4 py-6 flex flex-wrap items-center gap-3 justify-center">
                    <Link to="/catalog" className="btn-primary" data-testid="hero-browse-catalog">
                        {t("home.hero_browse")} <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/new" className="btn-outline" data-testid="hero-new">
                        {t("home.hero_new")}
                    </Link>
                </div>
            </section>

            {/* CATEGORY CATALOG */}
            <section className="max-w-[1440px] mx-auto px-4 py-16" data-testid="home-categories">
                <div className="flex items-end justify-between mb-10 gap-6">
                    <div>
                        <div className="label-kicker mb-2">{t("home.section_browse")}</div>
                        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
                            {t("home.section_catalog")}
                        </h2>
                    </div>
                    <Link
                        to="/catalog"
                        className="text-xs font-display font-bold uppercase tracking-wider text-slate-700 hover:text-[hsl(var(--primary))] border-b-2 border-slate-900 pb-1"
                    >
                        {t("home.view_all")}
                    </Link>
                </div>

                {categories === null ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <Skeleton key={i} className="aspect-[4/5]" />
                        ))}
                    </div>
                ) : categories.length === 0 ? (
                    <div className="border border-slate-300 p-12 text-center">
                        <p className="font-mono text-slate-600 mb-4">
                            {t("home.catalog_empty")}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {categories.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/catalog/${c.id}`}
                                className="group card-sharp flex flex-col fade-in-up"
                                style={{ animationDelay: `${Math.min(i * 30, 400)}ms` }}
                                data-testid={`category-card-${c.id}`}
                            >
                                <div className="aspect-square bg-slate-50 relative overflow-hidden">
                                    {c.image_url ? (
                                        <img
                                            src={c.image_url}
                                            alt={c.name}
                                            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                                            loading="lazy"
                                            onError={(e) => (e.currentTarget.style.display = "none")}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                            <Package className="w-16 h-16" />
                                        </div>
                                    )}
                                    <div className="absolute top-2 left-2 font-mono text-[10px] text-slate-500 bg-white/80 px-1.5 py-0.5">
                                        #{String((c.order && c.order > 0) ? c.order : (i + 1)).padStart(2, "0")}
                                    </div>
                                </div>
                                <div className="p-3 border-t border-slate-200 flex-1 flex flex-col">
                                    <div className="font-display font-bold text-sm uppercase tracking-tight clamp-2 text-slate-900 group-hover:text-[hsl(var(--primary))] transition-colors">
                                        {c.name}
                                    </div>
                                    <div className="mt-auto pt-2 font-mono text-[11px] text-slate-500 flex items-center justify-between">
                                        <span>{c.product_count || 0} {t("home.parts")}</span>
                                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* BRAND GALLERIES */}
            {(oemBrands.length > 0 || aftermarketBrands.length > 0) && (
                <section className="border-t border-slate-200 bg-slate-50" data-testid="home-brands">
                    <div className="max-w-[1440px] mx-auto px-4 py-14">
                        {oemBrands.length > 0 && (
                            <div className="mb-10">
                                <div className="flex items-end justify-between mb-6 gap-6">
                                    <div>
                                        <div className="label-kicker mb-2">{t("home.section_oem")}</div>
                                        <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">
                                            {t("home.brands_oem")}
                                        </h2>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" data-testid="home-oem-brand-grid">
                                    {oemBrands.map((b) => (
                                        <Link
                                            key={b.id}
                                            to={`/catalog?brand_id=${b.id}`}
                                            className="group bg-white border border-slate-200 hover:border-slate-900 transition-colors flex flex-col items-center justify-center p-3 aspect-[3/2]"
                                            data-testid={`home-oem-brand-${b.id}`}
                                            title={`${b.name} (${b.product_count || 0})`}
                                        >
                                            {b.logo_url ? (
                                                <img
                                                    src={b.logo_url}
                                                    alt={b.name}
                                                    className="max-h-12 max-w-[80%] object-contain grayscale group-hover:grayscale-0 transition"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="font-display font-bold uppercase text-sm">{b.name}</div>
                                            )}
                                            <div className="mt-1 font-mono text-[10px] text-slate-500 group-hover:text-slate-900 text-center truncate w-full">
                                                {b.name}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {aftermarketBrands.length > 0 && (
                            <div>
                                <div className="flex items-end justify-between mb-6 gap-6">
                                    <div>
                                        <div className="label-kicker mb-2">{t("home.section_aftermarket")}</div>
                                        <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">
                                            {t("home.brands_aftermarket")}
                                        </h2>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" data-testid="home-aftermarket-brand-grid">
                                    {aftermarketBrands.map((b) => (
                                        <Link
                                            key={b.id}
                                            to={`/catalog?aftermarket_brand_id=${b.id}`}
                                            className="group bg-white border border-amber-200 hover:border-amber-500 transition-colors flex flex-col items-center justify-center p-3 aspect-[3/2]"
                                            data-testid={`home-aftermarket-brand-${b.id}`}
                                            title={`${b.name} (${b.product_count || 0})`}
                                        >
                                            {b.logo_url ? (
                                                <img
                                                    src={b.logo_url}
                                                    alt={b.name}
                                                    className="max-h-12 max-w-[80%] object-contain grayscale group-hover:grayscale-0 transition"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="font-display font-bold uppercase text-sm">{b.name}</div>
                                            )}
                                            <div className="mt-1 font-mono text-[10px] text-amber-700 group-hover:text-amber-900 text-center truncate w-full">
                                                {b.name}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-20" />
                <div className="relative max-w-[1440px] mx-auto px-4 py-16 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="label-kicker text-slate-500 mb-3">{t("home.section_platform")}</div>
                        <h3 className="font-display font-black text-3xl md:text-4xl uppercase leading-tight">
                            {t("home.cta_title_1")}<br />
                            <span className="text-[hsl(var(--primary))]">{t("home.cta_title_2")}</span>
                        </h3>
                        <p className="mt-4 text-slate-400 max-w-lg">
                            {t("home.cta_body")}
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row md:justify-end gap-3">
                        <Link to="/catalog" className="btn-primary" data-testid="cta-browse">
                            {t("home.cta_open_catalog")}
                        </Link>
                        <Link
                            to="/new"
                            className="btn-outline !text-white !border-slate-700 hover:!border-[hsl(var(--primary))]"
                            data-testid="cta-new"
                        >
                            {t("home.cta_new_arrivals")}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
